import React from "react";
import styled, { ThemeProvider } from "onnit-ui/lib/themes/styled";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import theme from "onnit-ui/lib/themes/light";
import Box from "onnit-ui/lib/box/Box";
import Text, { ListItem } from "onnit-ui/lib/text";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import usePageData from "../../hooks/usePageData";
import { Img } from "onnit-ui/lib/image";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";
import MediaHero02 from "onnit-ui/lib/module/MediaHero02";
import ImgDualPortrait from "onnit-ui/lib/module/ImgDualPortrait";
import MediaCollage03 from "onnit-ui/lib/module/MediaCollage03";
import DaggerSymbol from "onnit-ui/lib/text/DaggerSymbol";
import Grid from "onnit-ui/lib/module/grid/12/Grid";
import TwelveColumn from "onnit-ui/lib/module/grid/12/Grid";
import ImageContentPortrait from "onnit-ui/lib/module/ImageContentPortrait";
import MediaCollage01 from "onnit-ui/lib/module/MediaCollage01";
import MediaCollage02 from "onnit-ui/lib/module/MediaCollage02";
import CopyDisplay02 from "onnit-ui/lib/module/CopyDisplay02";
import MultiColumnText from "onnit-ui/lib/text/MultiColumnText";
import ingredients from "./ingredients";
import MediaContentShowcase from "onnit-ui/lib/module/MediaContentShowcase";
import Video from "onnit-ui/lib/video/Video";
import BgImage from "onnit-ui/lib/image/BgImage";
import UpSell from "../../components/common/UpSell";
import InformedSport from "../../components/common/InformedSport";
import Sup from "onnit-ui/lib/text/Sup";

import { TextProps } from "onnit-ui/lib/text/Text";

const CustomList = styled.ul`
    list-style-type: disc;
`;

const CustomDagger = ({ fontSize = ["0.4em", "0.4em", "0.55em"], color = "black" }: TextProps) => {
    return (
        <Text display="inline" className="oui-sup" fontSize={fontSize} color={color}>
            <DaggerSymbol />
        </Text>
    );
};

const CustomListItem = ({
    text,
    withDagger = false,
    color = "white",
}: {
    text: string;
    withDagger?: boolean;
    color?: string;
}) => {
    return (
        <ListItem
            typeStyle="copyHero01"
            color={color}
            pt={[4, 4, 6]}
            px={[7, 8, 0]}
            textAlign={["center", "left", "left"]}
        >
            {text}
            {withDagger ? <CustomDagger color={color} /> : ""}
        </ListItem>
    );
};

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData, findProduct } = usePageData();

    if (!pageData || !onnitContext) return null;

    // Packaged Bundles Product Ids
    const SHROOM_TECH_SPORT = findProduct(4449);
    const CREATINE = findProduct(5587);
    const TOTAL_STRENGTH_AND_PERFORMANCE = findProduct(2826);


    const IMG_URL = onnitContext.url.cdn.static_images + "/product-page/total-nitric-oxide/refresh";
    const SCDN_URL = onnitContext.url.cdn.static + "/images/product-page/total-nitric-oxide/refresh";
    const IMG_PARAMS = "?q=20&fm=pjpg&auto=compress,format";
    const purple = "#832758";
    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop>
                    <ProductImageGrid>
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/gallery-total-nitric-oxide-wide-1.jpg${IMG_PARAMS}`}
                            alt="Total Nitric Oxide® Tub"
                            width={1760}
                            height={840}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/gallery-mango-fruit-2.png${IMG_PARAMS}`}
                            alt="Man holding beet root"
                            width={920}
                            height={920}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/gallery-beet-root-3.png${IMG_PARAMS}`}
                            alt="Plant leaf"
                            width={920}
                            height={920}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/gallery-plant-leaf-4.png${IMG_PARAMS}`}
                            alt="Total Nitric Oxide® Tub surrounded by ingredients"
                            width={1760}
                            height={840}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <ImgDualPortrait py={[6, 6, 6, 8]}>
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/male-athlete-resistance-bands.png${IMG_PARAMS}`}
                            alt="Male Athlete training with resistance bands"
                            width={928}
                            height={928}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/female-athlete-resistance-bands.png${IMG_PARAMS}`}
                            alt="Female Athlete Training with resistance band"
                            width={928}
                            height={1288}
                        />
                    </ImgDualPortrait>

                    <BgImage
                        backgroundPosition="top"
                        backgroundSize="cover"
                        src={`${IMG_URL}/ignite--bg.png${IMG_PARAMS}`}
                    >
                        <TwelveColumn>
                            <MediaHero02
                                my={[6, 6, 8, 10]}
                                gridColumn={["1 / -1", "1 / -1", "2 / span 11"]}
                                media={
                                    <Box
                                        mt={["auto", "-33%", "-33%", "-33%", "-31%"]}
                                        ml={["auto", "auto", "auto", "8%"]}
                                    >
                                        <Img
                                            src={`${IMG_URL}/ignite-your-pump--bg--desktop.png${IMG_PARAMS}`}
                                            alt={`Girl Working Out`}
                                            width={1648}
                                            height={456}
                                        />
                                    </Box>
                                }
                                headline={
                                    <Box>
                                        <Text color="white" typeStyle="headlineHero01">
                                            Ignite
                                        </Text>
                                        <Text color="white" typeStyle="headlineHero01">
                                            Your
                                        </Text>
                                        <Text color="white" typeStyle="headlineHero01">
                                            Pump
                                        </Text>
                                    </Box>
                                }
                                content={
                                    <TwelveColumn my={[4, 4, 6, 8]}>
                                        <Box gridColumn={["2 /span 10"]}>
                                            <Text typeStyle="copyHero01" color="white">
                                                Designed to support your body’s natural nitric oxide production to
                                                promote performance, recovery, and growth.
                                                <span className="oui-sup">
                                                    <DaggerSymbol />
                                                </span>
                                            </Text>
                                        </Box>
                                    </TwelveColumn>
                                }
                            />
                        </TwelveColumn>
                        <MediaCollage03
                            mt={[5, 5, 5, 8]}
                            pb={[8, 8, 8, 10]}
                            item1={
                                <Img
                                    src={`${IMG_URL}/ignite-b.png${IMG_PARAMS}`}
                                    alt="Guy working out with chains"
                                    width={1170}
                                    height={656}
                                />
                            }
                            item2={
                                <Img
                                    src={`${IMG_URL}/ignite-c.png${IMG_PARAMS}`}
                                    alt="Girl kickboxing"
                                    width={448}
                                    height={448}
                                />
                            }
                            item3={
                                <Img
                                    src={`${IMG_URL}/ignite-d.png${IMG_PARAMS}`}
                                    alt="Girl doing landmines with resistance band"
                                    width={688}
                                    height={388}
                                />
                            }
                        />
                    </BgImage>

                    <ImageContentPortrait
                        mt={[6, 6, 8, 10]}
                        image={
                            <Img
                                src={`${IMG_URL}/get-more.png${IMG_PARAMS}`}
                                alt="Tennis player sitting on bench"
                                width={928}
                                height={1200}
                            />
                        }
                        content={
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text color={purple} typeStyle="copyDisplay02" mb={[4, 4, 6]}>
                                    Get More Out Of Your Workouts
                                </Text>
                                <Text as="p" typeStyle="copyHero02" color="grays.6">
                                    Getting the best results possible from your workouts often comes down to the finer
                                    details. One of these finer details for the average person to reap the benefits of
                                    exercise is to optimize nitric oxide (NO), a crucial component of overall health,
                                    performance, and recovery that’s often overlooked.
                                    <span className="oui-sup">
                                        <DaggerSymbol />
                                    </span>
                                </Text>
                            </Box>
                        }
                    />
                    <MediaCollage01
                        mt={[6, 6, 8, 10]}
                        item1={
                            <Img
                                src={`${IMG_URL}/get-more-b.png${IMG_PARAMS}`}
                                alt="Tennis player serving ball"
                                width={448}
                                height={692}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/get-more-c.png${IMG_PARAMS}`}
                                alt="Tennis playing hitting ball"
                                width={448}
                                height={672}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/get-more-d.png${IMG_PARAMS}`}
                                alt="Tennis player waiting for ball"
                                width={928}
                                height={1280}
                            />
                        }
                    />
                    <Grid my={[6, 6, 8, 10]} px={4}>
                        <Box gridColumn={["1 / -1", "2 / span 10"]} py={[6, 6, 6, 8]}>
                            <Text typeStyle="copyHero01" color="grays.6">
                                We developed the best nitric oxide formulation we could, so you can get the most out of
                                your workouts.
                            </Text>
                        </Box>
                    </Grid>

                    <CopyDisplay02 pb={8} dashColor="black">
                        <Text color={purple} textTransform="caps" typeStyle="copyDisplay02" mb={[6, 6, 6, 8]}>
                            Performance and pump: How Total Nitric Oxide
                            <Sup variant="reg" /> Works
                        </Text>
                        <MultiColumnText>
                            <Text as="p" typeStyle="copyHero02" color="grays.6" mb={4}>
                                Nitric oxide is a naturally-occurring compound, synthesized in the body from the amino
                                acids L-arginine and L-citrulline. It is a vasodilator, meaning that it dilates (or
                                widens) the blood vessels, allowing blood to flow throughout the body with less
                                restriction.
                                <span className="oui-sup">
                                    <DaggerSymbol />
                                </span>
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.6" mb={4}>
                                Boosting your body’s nitric oxide production is like shifting into a higher gear during
                                intense exercise, ensuring that your muscles are provided with the oxygen and nutrients
                                needed while removing excess waste that may cause fatigue — promoting performance,
                                recovery, and growth.
                                <span className="oui-sup">
                                    <DaggerSymbol />
                                </span>
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.6" mb={4}>
                                Taking Total Nitric Oxide
                                <Sup variant="reg" /> regularly throughout the day also supports cellular integrity, as
                                well as cardiovascular health when combined with a healthy, active lifestyle.
                                <span className="oui-sup">
                                    <DaggerSymbol />
                                </span>
                            </Text>
                        </MultiColumnText>
                    </CopyDisplay02>

                    <BgImage backgroundSize="cover" src={`${IMG_URL}/jihn-red--bg.png${IMG_PARAMS}`}>
                        <TwelveColumn>
                            <Box gridColumn={["1 / -1", "2 / span 12"]} mt={6} pt={[10, 10, 10, 10, 14]} px={[4, 0, 0]}>
                                <Text typeStyle="copyHero01" color="white">
                                    <CustomList>
                                        <CustomListItem
                                            text="Helps circulation via N.O. pathway support"
                                            color="white"
                                            withDagger={true}
                                        />
                                        <CustomListItem
                                            text="Aids the delivery of oxygen and nutrients to working tissue"
                                            color="white"
                                            withDagger={true}
                                        />
                                        <CustomListItem
                                            text="Promotes cellular integrity"
                                            color="white"
                                            withDagger={true}
                                        />
                                    </CustomList>
                                </Text>
                            </Box>
                            <Box gridColumn={["1 / -1", "5 / span 7"]} py={[6, 6, 8, 10]}>
                                <Video playsInline autoPlay muted loop placeholder={[1032, 582]}>
                                    <source
                                        src={`${SCDN_URL}/jihn-yu-fre-action.mp4`}
                                        type="video/mp4"
                                        title="Jihn Yu shadow boxing"
                                    />
                                </Video>
                            </Box>
                        </TwelveColumn>
                    </BgImage>
                    <Box pt={[8, 8, 8, 10]} backgroundColor="black">
                        <Grid mb={4}>
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 12"]} pt={3} px={5}>
                                <Text as="p" typeStyle="copyDisplay02" color="white">
                                    Key Ingredients
                                </Text>
                            </Box>
                        </Grid>
                        <MediaContentShowcase
                            mb={[6]}
                            items={ingredients.map((x) => {
                                let copy = { ...x };
                                copy.image = `${IMG_URL}/${x.image}`;
                                return copy;
                            })}
                            textColor="white"
                            activeColor={purple}
                            bgMedia={
                                <Video playsInline autoPlay muted loop>
                                    <source
                                        src={`${SCDN_URL}/key-ingredients--bg.mp4`}
                                        type="video/mp4"
                                        title="Key Ingredients Background"
                                        aria-hidden="true"
                                    />
                                </Video>
                            }
                        />
                        <MediaCollage02
                            py={[8, 8, 8, 10]}
                            item1={
                                <Img
                                    src={`${IMG_URL}/coastline.png${IMG_PARAMS}`}
                                    alt="Coastline"
                                    width={448}
                                    height={336}
                                />
                            }
                            item2={
                                <Img src={`${IMG_URL}/beets.png${IMG_PARAMS}`} alt="Beets" width={688} height={688} />
                            }
                            item3={
                                <Img
                                    src={`${IMG_URL}/mango-tree.png${IMG_PARAMS}`}
                                    alt="Mango Tree"
                                    width={1168}
                                    height={876}
                                />
                            }
                        />
                    </Box>

                    <Box py={[6, 6, 6, 8]} bg={purple}>
                        <Box
                            px={[4, 4, 4, 10]}
                            pt={[4, 6, 6, 8]}
                            textAlign={["left", "left", "left", "center"]}
                            mx="auto"
                        >
                            <Text typeStyle="copyDisplay02" lineHeight={1} color="white" fontSize={[6, 8, 10, 12]}>
                                TOTAL NITRIC OXIDE
                                <Sup variant="reg" /> COMBOS
                            </Text>
                            <Text
                                as="p"
                                typeStyle="copyHero02"
                                color="white"
                                fontSize={[2, 4, 4]}
                                mb={[4, 4, 4, 6]}
                                mt={4}
                                maxWidth="900px"
                                mx="auto"
                            >
                                Get the most out of Total Nitric Oxide
                                <Sup variant="reg" /> holistically. We recommend the following products to support
                                cognitive performance and overall well-being:
                            </Text>
                        </Box>

                        {SHROOM_TECH_SPORT && (
                            <UpSell
                                pid={SHROOM_TECH_SPORT.id}
                                price={SHROOM_TECH_SPORT.price}
                                msrp={SHROOM_TECH_SPORT.price_msrp}
                                inStock={SHROOM_TECH_SPORT.in_stock}
                                showPercentageSaved={true}
                                discountColor="white"
                                img={
                                    <Img
                                        src={`${IMG_URL}/UpSell-STS.jpg${IMG_PARAMS}`}
                                        alt="Shroom Tech® SPORT"
                                        width={928}
                                        height={928}
                                    />
                                }
                                title={
                                    <>
                                        Shroom Tech
                                        <Sup variant="reg" /> SPORT
                                    </>
                                }
                                subtext="Shroom Tech® SPORT (84 ct)"
                                description={
                                    <>
                                        Designed to help the body with cellular energy and cardiovascular endurance
                                        <Sup variant="dagger" />, Shroom Tech
                                        <Sup variant="reg" /> SPORT is the perfect pre-workout pairing with your Total
                                        Nitric Oxide
                                        <Sup variant="reg" />.
                                    </>
                                }
                            />
                        )}

                        {TOTAL_STRENGTH_AND_PERFORMANCE && (
                            <UpSell
                                pid={TOTAL_STRENGTH_AND_PERFORMANCE.id}
                                price={TOTAL_STRENGTH_AND_PERFORMANCE.price}
                                msrp={TOTAL_STRENGTH_AND_PERFORMANCE.price_msrp}
                                inStock={TOTAL_STRENGTH_AND_PERFORMANCE.in_stock}
                                showPercentageSaved={true}
                                discountColor="white"
                                img={
                                    <Img
                                        src={`${IMG_URL}/UpSell-TotalStrength.jpg${IMG_PARAMS}`}
                                        alt="Total Strength and Performance"
                                        width={928}
                                        height={928}
                                    />
                                }
                                title={<>Total Strength &amp; Performance</>}
                                subtext="Total Strength and Performance"
                                description={
                                    <>
                                        A caffeine-free formula designed to aid physical performance, and promote
                                        strength &amp; power.
                                        <Sup variant="dagger" /> When paired with Total Nitric Oxide
                                        <Sup variant="reg" /> this duo delivers performance nutrition where it matters
                                        most.
                                    </>
                                }
                            />
                        )}

                        {CREATINE && (
                            <UpSell
                                pid={CREATINE.id}
                                price={CREATINE.price}
                                msrp={CREATINE.price_msrp}
                                inStock={CREATINE.in_stock}
                                showPercentageSaved={true}
                                discountColor="white"
                                img={
                                    <Img
                                        src={`${IMG_URL}/2023-TNO-wpage-images-upsell-creatine.jpg${IMG_PARAMS}`}
                                        alt="Total Nitric Oxide tub next to creatine tub."
                                        width={928}
                                        height={928}
                                    />
                                }
                                title={<>Creatine</>}
                                subtext="Creatine"
                                description={
                                    <>
                                        Pairing Total Nitric Oxide
                                        <Sup variant="reg" /> is a must for anyone spending any significant time in the
                                        gym. One of the most popular sports nutrition supplements in the world, Creatine
                                        is shown to support muscle gain, strength, and anaerobic performance.
                                        <Sup variant="dagger" />
                                    </>
                                }
                            />
                        )}
                    </Box>

                    <InformedSport />
                </div>

                <UsageAndDisclaimers supplementFacts={<SupplementFacts />} />
            </>
        </ThemeProvider>
    );
}

export default Page;
